import React from 'react';
import { Users, FileText } from 'lucide-react';

export const BulkUploadSection = () => (
  <>
    <h3 className="text-lg font-semibold mb-3">Bulk Upload Features</h3>

    <div className="mb-6">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <Users className="w-4 h-4" />
        Family Bulk Upload
      </h4>
      <p className="text-sm mb-2"><strong>Location:</strong> Admin Panel → Families tab → "Bulk Upload Families" section</p>

      <p className="text-sm font-semibold mb-2">Steps:</p>
      <ol className="list-decimal list-inside space-y-2 text-sm ml-4">
        <li>Click "Download CSV Template" to get the correct format</li>
        <li>Open template in Excel or Google Sheets</li>
        <li>Fill in family details:
          <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
            <li><strong>family_id</strong>: Unique identifier (e.g., SEC-2413)</li>
            <li><strong>principle_member_name</strong>: Head of family name</li>
            <li><strong>total_allotment</strong>: Total insurance coverage (USD)</li>
            <li><strong>remaining_balance</strong>: Available balance (usually same as allotment)</li>
          </ul>
        </li>
        <li>Save as CSV file</li>
        <li>Click "Choose File" and select your CSV</li>
        <li>Review the preview table showing all families to be added</li>
        <li>Click "Upload Families" to complete the import</li>
      </ol>

      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3 text-sm">
        <strong>⚠️ Important Notes:</strong>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Family IDs must be unique across the system</li>
          <li>All amounts should be numeric values (no currency symbols)</li>
          <li>Preview carefully before uploading - there's no undo!</li>
        </ul>
      </div>
    </div>

    <div className="mb-6">
      <h4 className="font-semibold mb-2 flex items-center gap-2">
        <FileText className="w-4 h-4" />
        Price List Bulk Upload
      </h4>
      <p className="text-sm mb-2"><strong>Location:</strong> Admin Panel → Price Lists tab → "Bulk Upload Price List" section</p>

      <p className="text-sm font-semibold mb-2">Steps:</p>
      <ol className="list-decimal list-inside space-y-2 text-sm ml-4">
        <li>Select the hospital from the dropdown (required!)</li>
        <li>Click "Download CSV Template" to get the correct format</li>
        <li>Open template in Excel or Google Sheets</li>
        <li>Fill in service details:
          <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
            <li><strong>item_id</strong>: Unique service code (e.g., CONS-001)</li>
            <li><strong>item_name</strong>: Service name (e.g., "General Consultation")</li>
            <li><strong>item_type</strong>: Category (e.g., "Consultation", "Lab Test")</li>
            <li><strong>cost</strong>: Price in hospital's currency</li>
          </ul>
        </li>
        <li>Save as CSV file</li>
        <li>Click "Choose File" and select your CSV</li>
        <li>Click "Upload Items" to complete the import</li>
      </ol>

      <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mt-3 text-sm">
        <strong>⚠️ Important Notes:</strong>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>Must select a hospital before uploading</li>
          <li>Item IDs must be unique within each hospital</li>
          <li>Costs are in the hospital's local currency (SSP, USD, etc.)</li>
          <li>Duplicate item IDs will fail the upload</li>
        </ul>
      </div>
    </div>

    <div>
      <h4 className="font-semibold mb-2">Troubleshooting</h4>
      <ul className="list-disc list-inside space-y-2 text-sm ml-4">
        <li><strong>Upload failed</strong>: Check CSV format matches template exactly</li>
        <li><strong>Duplicate ID error</strong>: Ensure all IDs are unique</li>
        <li><strong>No preview shown</strong>: Verify file is valid CSV format</li>
        <li><strong>Missing hospital error</strong>: Make sure to select hospital before uploading price lists</li>
      </ul>
    </div>
  </>
);
